import { getPixelRatio } from './common';

interface TextItem {
    str: string;
    transform: number[];
    width: number;
    height: number;
    fontName?: string;
}

export interface IStaticInfo {
    page: number;
    index: number;
    keyword: string;
}

export interface IApplyHighlightOptions {
    selectedMatchIndex?: number;
    selectedClassName?: string;
    normalClassName?: string;
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

type BoundaryEndIndexSet = Set<number>;
type HyphenDropMode = 'never' | 'boundary-if-term-without-hyphen';

/**
 * 干扰字符正则：涵盖 C0/C1 控制符、零宽字符、BOM、方向标记 (LRM/RLM)、方向嵌入/覆盖标记等
 * 这些字符在 PDF 提取中常见，视觉不可见但会打断文本连续性
 */
const IGNORABLE_CHAR_REGEX = /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF\u200E\u200F\u202A-\u202E]/;

function isIgnorableChar(ch: string): boolean {
    return /\s/.test(ch) || IGNORABLE_CHAR_REGEX.test(ch);
}

function isSoftHyphen(ch: string): boolean {
    // PDF 文本提取中较常见，视觉上可能不显示，但会破坏纯字符串匹配
    return ch === '\u00ad';
}

function isHyphenLike(ch: string): boolean {
    // 仅包含“连字符”语义的常见字符（不包含 en-dash/em-dash 等）
    return ch === '-' || ch === '\u2010' || ch === '\u2011' || ch === '\u2012';
}

function hasHyphenLike(text: string): boolean {
    for (let i = 0; i < text.length; i++) {
        if (isHyphenLike(text[i])) {
            return true;
        }
    }
    return false;
}

function isAsciiLetter(ch: string): boolean {
    if (!ch) {
        return false;
    }
    const code = ch.charCodeAt(0);
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

function normalizeTermForSearch(rawTerm: string): string {
    // 方案 A：忽略空白与不可见字符；不主动剔除普通连字符（避免 well-known => wellknown 的默认“误通配”）
    // 使用 NFKC 归一化抹平字符差异（如全角转半角、合体字分解等）
    const term = rawTerm.normalize('NFKC').toLowerCase();
    let out = '';
    for (let i = 0; i < term.length; i++) {
        const ch = term[i];
        if (isIgnorableChar(ch) || isSoftHyphen(ch)) {
            continue;
        }
        out += ch;
    }
    return out;
}

function shouldDropHyphenAtBoundary(rawText: string, rawIdx: number, boundaryEnds?: BoundaryEndIndexSet): boolean {
    if (!boundaryEnds || !boundaryEnds.has(rawIdx)) {
        return false;
    }
    const ch = rawText[rawIdx];
    if (!isHyphenLike(ch)) {
        return false;
    }

    // 仅在“分段末尾连字符 + 下一段紧接字母”的情况下把连字符当作断词连字符丢弃：
    // - 这在不引入布局信息（方案 B）的前提下，能显著降低对 well-known 这类真实连字符单词的误合并风险
    let prev: string | undefined;
    for (let i = rawIdx - 1; i >= 0; i--) {
        const p = rawText[i];
        if (isIgnorableChar(p) || isSoftHyphen(p)) {
            continue;
        }
        prev = p;
        break;
    }
    let next: string | undefined;
    for (let i = rawIdx + 1; i < rawText.length; i++) {
        const n = rawText[i];
        if (isIgnorableChar(n) || isSoftHyphen(n)) {
            continue;
        }
        next = n;
        break;
    }
    return isAsciiLetter(prev || '') && isAsciiLetter(next || '');
}

function buildNormalizedTextAndMap(
    rawText: string,
    options?: { boundaryEnds?: BoundaryEndIndexSet; hyphenDropMode?: HyphenDropMode },
): { normalizedText: string; normIndexToRawIndex: number[] } {
    const boundaryEnds = options?.boundaryEnds;
    const hyphenDropMode: HyphenDropMode = options?.hyphenDropMode || 'never';
    const chars: string[] = [];
    const normIndexToRawIndex: number[] = [];
    for (let rawIdx = 0; rawIdx < rawText.length; rawIdx++) {
        const ch = rawText[rawIdx];
        if (isIgnorableChar(ch) || isSoftHyphen(ch)) {
            continue;
        }
        // 注意：连字符丢弃策略必须非常谨慎，否则会破坏对真实连字符单词（例如 well-known）的搜索。
        const shouldConsiderDrop =
            hyphenDropMode === 'boundary-if-term-without-hyphen' &&
            isHyphenLike(ch) &&
            shouldDropHyphenAtBoundary(rawText, rawIdx, boundaryEnds);
        if (shouldConsiderDrop) {
            continue;
        }

        // 进行 Unicode 归一化处理（处理 CJK 兼容字符及拉丁合体字）
        const normalizedCh = ch.normalize('NFKC');
        for (let j = 0; j < normalizedCh.length; j++) {
            chars.push(normalizedCh[j]);
            // 关键：映射回原始 rawText 的索引，确保高亮位置准确
            normIndexToRawIndex.push(rawIdx);
        }
    }
    return { normalizedText: chars.join(''), normIndexToRawIndex };
}

function calcWhitespaceWidthStyle(
    span: HTMLSpanElement,
    domIndex: number,
    spanArray: HTMLSpanElement[],
    originalTextContent: string,
    matchedText: string,
): string {
    const curLeft = Number.parseFloat(span.style.left || '');
    if (!Number.isFinite(curLeft)) return '';

    let nextLeft: number | undefined;
    for (let j = domIndex + 1; j < spanArray.length; j++) {
        const nl = Number.parseFloat(spanArray[j].style.left || '');
        if (Number.isFinite(nl) && nl > curLeft + 0.01) {
            nextLeft = nl;
            break;
        }
    }
    if (typeof nextLeft !== 'number' || nextLeft <= curLeft) return '';

    const runWidth = Math.max(0, nextLeft - curLeft - 0.2);
    if (runWidth <= 0) return '';

    const spanIsAllWhitespace = originalTextContent.length > 0 && originalTextContent.trim().length === 0;
    if (spanIsAllWhitespace && originalTextContent.length > matchedText.length) {
        const w = Math.max(0, (runWidth / originalTextContent.length) * matchedText.length);
        return `width:${w}px;`;
    }
    return `width:${runWidth}px;`;
}

function buildHorizontalHighlightHtml(
    originalTextContent: string,
    spanText: string,
    spanStart: number,
    overlappingMatches: { start: number; end: number; idx: number }[],
    selectedMatchIndex: number | undefined,
    selectedClassName: string,
    normalClassName: string,
    span: HTMLSpanElement,
    domIndex: number,
    spanArray: HTMLSpanElement[],
): string {
    let html = '';
    let lastIndex = 0;

    overlappingMatches.forEach((match) => {
        const relativeStart = Math.max(0, match.start - spanStart);
        const relativeEnd = Math.min(spanText.length, match.end - spanStart);

        if (relativeStart > lastIndex) {
            html += escapeHtml(originalTextContent.substring(lastIndex, relativeStart));
        }

        const matchedText = originalTextContent.substring(relativeStart, relativeEnd);
        const isSelected = match.idx === Number(selectedMatchIndex);
        const cls = `pdf-content-text-search-highlight ${isSelected ? selectedClassName : normalClassName}`;

        if (matchedText.length > 0 && matchedText.trim().length === 0) {
            const nbsp = '&nbsp;'.repeat(matchedText.length);
            const widthStyle = calcWhitespaceWidthStyle(span, domIndex, spanArray, originalTextContent, matchedText);
            html += `<mark class="${cls}" data-pdf-search-idx="${match.idx}" style="display:inline-block;white-space:pre;${widthStyle}">${nbsp}</mark>`;
        } else {
            html += `<mark class="${cls}" data-pdf-search-idx="${match.idx}">${escapeHtml(matchedText)}</mark>`;
        }

        lastIndex = relativeEnd;
    });

    if (lastIndex < originalTextContent.length) {
        html += escapeHtml(originalTextContent.substring(lastIndex));
    }

    return html;
}

export function applyHighlightsToDom(container: HTMLElement, searchTerm: string, options?: IApplyHighlightOptions) {
    if (!searchTerm.trim()) {
        const allTextSpans = container.querySelectorAll('[data-text]');
        allTextSpans.forEach((span: Element) => {
            const htmlSpan = span as HTMLSpanElement;
            const originalText = htmlSpan.getAttribute('data-original-text') || '';
            htmlSpan.innerHTML = '';
            htmlSpan.textContent = originalText;
            htmlSpan.style.opacity = '1';
            htmlSpan.style.color = 'transparent';
            htmlSpan.style.backgroundColor = '';
            htmlSpan.style.mixBlendMode = 'multiply';
        });
        container.removeAttribute('data-applied-search-term');
        return;
    }

    const { selectedMatchIndex, selectedClassName = 'selected', normalClassName = 'normal' } = options || {};
    const term = searchTerm.toLowerCase().trim();
    const normalizedTerm = normalizeTermForSearch(term);
    if (!normalizedTerm) {
        // 例如输入全是空白/不可见字符：与“清空搜索”一致
        const allTextSpans = container.querySelectorAll('[data-text]');
        allTextSpans.forEach((span: Element) => {
            const htmlSpan = span as HTMLSpanElement;
            const originalText = htmlSpan.getAttribute('data-original-text') || '';
            htmlSpan.innerHTML = '';
            htmlSpan.textContent = originalText;
            htmlSpan.style.opacity = '1';
            htmlSpan.style.color = 'transparent';
            htmlSpan.style.backgroundColor = '';
            htmlSpan.style.mixBlendMode = 'multiply';
        });
        container.removeAttribute('data-applied-search-term');
        return;
    }
    const textSpans = container.querySelectorAll('[data-text]');

    // Build a map of text content and span indices
    const spanArray = Array.from(textSpans) as HTMLSpanElement[];
    let fullText = '';
    const boundaryEnds: BoundaryEndIndexSet = new Set();
    const spanPositions: {
        start: number;
        end: number;
        span: HTMLSpanElement;
        dataText: string;
        domIndex: number;
    }[] = [];

    // Concatenate all text and track positions
    spanArray.forEach((span, domIndex) => {
        // Always use data-text attribute which stores the original text
        const dataText = span.getAttribute('data-text') || '';
        const start = fullText.length;
        const end = start + dataText.length;
        spanPositions.push({ start, end, span, dataText, domIndex });
        fullText += dataText;
        if (dataText.length > 0) {
            boundaryEnds.add(end - 1);
        }
    });

    // 方案 A：构建归一化文本流 + 回溯映射，用于支持跨行/断词匹配（空白忽略、行尾断词连字符处理）
    const termHasHyphen = hasHyphenLike(normalizedTerm);
    const { normalizedText, normIndexToRawIndex } = buildNormalizedTextAndMap(fullText, {
        boundaryEnds,
        hyphenDropMode: termHasHyphen ? 'never' : 'boundary-if-term-without-hyphen',
    });

    // Find all matches in the normalized text (then map back to raw indices for highlight)
    const matches: { start: number; end: number; idx: number }[] = [];
    let searchIndex = 0;
    let matchIdx = 0;

    while (searchIndex < normalizedText.length) {
        const matchIndex = normalizedText.indexOf(normalizedTerm, searchIndex);
        if (matchIndex === -1) {
            break;
        }

        const normStart = matchIndex;
        const normEnd = matchIndex + normalizedTerm.length;
        const rawStart = normIndexToRawIndex[normStart];
        const rawEndLastRawIdx = normIndexToRawIndex[normEnd - 1];
        if (rawStart === undefined || rawEndLastRawIdx === undefined) {
            // 理论上不会发生：normalizedText 与映射数组一一对应；这里做兜底避免运行时异常
            searchIndex = matchIndex + 1;
            continue;
        }
        const rawEnd = rawEndLastRawIdx + 1;

        matches.push({
            start: rawStart,
            end: rawEnd,
            idx: matchIdx,
        });
        matchIdx++;
        searchIndex = matchIndex + 1;
    }

    spanPositions.forEach((pos) => {
        const span = pos.span;
        const spanText = pos.dataText;
        const spanStart = pos.start;
        const spanEnd = pos.end;
        const domIndex = pos.domIndex;

        const originalTextContent = span.getAttribute('data-original-text') || '';
        const overlappingMatches = matches.filter((match) => match.start < spanEnd && match.end > spanStart);

        if (overlappingMatches.length === 0) {
            span.innerHTML = '';
            span.textContent = originalTextContent;
            span.style.opacity = '1';
            span.style.color = 'transparent';
            span.style.backgroundColor = '';
            span.style.mixBlendMode = 'multiply';
            return;
        }

        const isVerticalSpan = span.hasAttribute('data-vertical');

        if (isVerticalSpan) {
            // 竖排：绝对定位 overlay 方案
            // display:inline / display:inline-block 在 writing-mode:vertical-rl 中背景都受字体
            // ascent/descent metrics 影响，实际渲染高度超过字符数 × charHeight。
            // 改为在 span（position:absolute 已建立 containing block）内插入
            // position:absolute 的 mark，用 --vertical-char-height 精确控制 top + height，
            // 与文本流完全解耦，消除 metrics 干扰。
            span.innerHTML = '';
            span.textContent = originalTextContent;

            overlappingMatches.forEach((match) => {
                const relativeStart = Math.max(0, match.start - spanStart);
                const relativeEnd = Math.min(spanText.length, match.end - spanStart);
                const matchLen = relativeEnd - relativeStart;
                if (matchLen <= 0) return;

                const isSelected = match.idx === Number(selectedMatchIndex);
                const expandClassName = isSelected ? selectedClassName : normalClassName;
                const overlayMark = document.createElement('mark');
                overlayMark.className = `pdf-content-text-search-highlight ${expandClassName}`;
                overlayMark.setAttribute('data-pdf-search-idx', String(match.idx));
                overlayMark.style.position = 'absolute';
                overlayMark.style.top = `calc(${relativeStart} * var(--vertical-char-height, 1em))`;
                overlayMark.style.height = `calc(${matchLen} * var(--vertical-char-height, 1em))`;
                overlayMark.style.left = '0';
                overlayMark.style.width = '100%';
                overlayMark.style.boxSizing = 'border-box';
                span.appendChild(overlayMark);
            });
        } else {
            span.innerHTML = buildHorizontalHighlightHtml(
                originalTextContent,
                spanText,
                spanStart,
                overlappingMatches,
                selectedMatchIndex,
                selectedClassName,
                normalClassName,
                span,
                domIndex,
                spanArray,
            );
        }

        span.style.opacity = '1';
        span.style.color = 'transparent';
        span.style.backgroundColor = '';
        span.style.mixBlendMode = 'normal';
    });
    container.setAttribute('data-applied-search-term', searchTerm);
}

/**
 * 仅更新选中状态，不重新渲染整个文本层（高性能）
 */
export function updateSelectedHighlight(container: HTMLElement, selectedMatchIndex?: number) {
    // 移除之前的选中态
    const prevSelected = container.querySelectorAll('.pdf-content-text-search-highlight.selected');
    prevSelected.forEach((el) => {
        el.classList.remove('selected');
        el.classList.add('normal');
    });

    // 添加新的选中态
    if (typeof selectedMatchIndex === 'number') {
        const newSelected = container.querySelectorAll(
            `.pdf-content-text-search-highlight[data-pdf-search-idx="${selectedMatchIndex}"]`,
        );
        newSelected.forEach((el) => {
            el.classList.remove('normal');
            el.classList.add('selected');
        });
    }
}

export function findMatchesInPage(
    pageNum: number,
    textContent: any,
    term: string,
    originalTerm: string,
): IStaticInfo[] {
    let fullText = '';
    const boundaryEnds: BoundaryEndIndexSet = new Set();
    let cursor = 0;
    // biome-ignore lint/suspicious/noExplicitAny: 111
    textContent.items.forEach((item: any) => {
        // 参考实现的方式：检查 'str' in item
        if ('str' in item && item.str) {
            const s = String(item.str).toLowerCase();
            fullText += s;
            if (s.length > 0) {
                boundaryEnds.add(cursor + s.length - 1);
                cursor += s.length;
            }
        }
    });

    const matches: IStaticInfo[] = [];
    const normalizedTerm = normalizeTermForSearch(term);
    if (!normalizedTerm) {
        return matches;
    }
    const termHasHyphen = hasHyphenLike(normalizedTerm);
    const { normalizedText } = buildNormalizedTextAndMap(fullText, {
        boundaryEnds,
        hyphenDropMode: termHasHyphen ? 'never' : 'boundary-if-term-without-hyphen',
    });
    let searchIndex = 0;
    let matchIndexOnPage = 0;

    while (searchIndex < normalizedText.length) {
        const matchIndex = normalizedText.indexOf(normalizedTerm, searchIndex);
        if (matchIndex === -1) {
            break;
        }

        matches.push({
            page: pageNum,
            index: matchIndexOnPage,
            keyword: originalTerm,
        });
        matchIndexOnPage++;
        searchIndex = matchIndex + 1;
    }
    return matches;
}

/** 竖排判定：基线角度与 ±90° 的容差（弧度），超过则视为竖排文案 */
const VERTICAL_BASELINE_TOLERANCE = 0.35;

function isVerticalBaseline(tx: number[]): boolean {
    if (!tx || tx.length < 4) return false;
    const angle = Math.atan2(tx[1], tx[0]);
    return Math.abs(Math.abs(angle) - Math.PI / 2) < VERTICAL_BASELINE_TOLERANCE;
}

function createTextSpan(item: TextItem, viewport: any, scaleX: number = 1, scaleY: number = 1): HTMLSpanElement {
    const tx = item.transform;
    const textSpan = document.createElement('span');
    textSpan.textContent = item.str;
    const isWhitespaceOnly = item.str.length > 0 && item.str.trim().length === 0;
    textSpan.className = 'absolute whitespace-pre';
    textSpan.setAttribute('data-text', item.str.toLowerCase());
    textSpan.setAttribute('data-original-text', item.str); // Store original text with casing

    // Disable text selection
    textSpan.style.userSelect = 'none';
    textSpan.style.pointerEvents = 'none';
    // 显式设置 position: absolute，确保 left 和 top 生效
    textSpan.style.position = 'absolute';

    const fontHeight = Math.hypot(tx[1], tx[3]);
    const pdfX = tx[4];
    const pdfY = tx[5];
    const pdfWidth = item.width;
    const pdfHeight = fontHeight;

    const p0 = viewport.convertToViewportPoint(pdfX, pdfY) as [number, number];
    const x0 = p0[0];
    const y0 = p0[1];

    const p1 = viewport.convertToViewportPoint(pdfX + pdfWidth, pdfY) as [number, number];
    const x1 = p1[0];
    const y1 = p1[1];

    const p2 = viewport.convertToViewportPoint(pdfX, pdfY + pdfHeight) as [number, number];
    const x2 = p2[0];
    const y2 = p2[1];

    const canvasWidth = Math.hypot(x1 - x0, y1 - y0) * scaleX;
    const canvasHeight = Math.hypot(x2 - x0, y2 - y0) * scaleY;
    const angle = Math.atan2(y1 - y0, x1 - x0);
    const verticalByBaseline = isVerticalBaseline(tx);

    textSpan.style.left = `${x0 * scaleX}px`;
    textSpan.style.top = `${y0 * scaleY}px`;
    textSpan.style.fontSize = `${canvasHeight}px`;
    textSpan.style.fontFamily = item.fontName || 'sans-serif';
    textSpan.style.lineHeight = '1.0';
    textSpan.style.whiteSpace = 'pre';
    textSpan.style.margin = '0';
    textSpan.style.padding = '0';
    textSpan.style.height = `${canvasHeight}px`;

    const transforms: string[] = [];
    const translateYValue = canvasHeight * 0.83;
    transforms.push(`translateY(-${translateYValue}px)`);
    if (!verticalByBaseline && Math.abs(angle) > 0.001) {
        transforms.push(`rotate(${angle}rad)`);
    }
    if (transforms.length > 0) {
        textSpan.style.transform = transforms.join(' ');
        textSpan.style.transformOrigin = '0 100%';
    }

    textSpan.style.visibility = 'hidden';
    document.body.appendChild(textSpan);
    const naturalWidth = textSpan.getBoundingClientRect().width;
    document.body.removeChild(textSpan);
    textSpan.style.visibility = 'visible';

    // 判定是否按竖排展示：1) transform 基线接近 ±90° 或 2) 横向时 PDF 宽度极窄（会得到很小 scaleX），多为竖排内容
    const wouldApplyScaleX =
        !isWhitespaceOnly && naturalWidth > 0 && canvasWidth > 0 && Math.abs(naturalWidth - canvasWidth) > 0.1;
    const wouldBeScaleX = wouldApplyScaleX ? canvasWidth / naturalWidth : 1;
    const vertical =
        verticalByBaseline || (wouldApplyScaleX && wouldBeScaleX > 0 && wouldBeScaleX < 0.5);

    if (vertical) {
        // 竖排：用 writing-mode 让文案竖着排列，去掉 rotate/scaleX
        const charCount = Math.max(1, item.str.length);
        const verticalBlockWidth = canvasWidth;
        // 高度不足原因：canvasHeight 来自 PDF transform，为“设计字高”；浏览器渲染时实际行高常多出约 5–8px
        const VERTICAL_HEIGHT_EXTRA_PX = 8;
        const verticalBlockHeight = canvasHeight * charCount + VERTICAL_HEIGHT_EXTRA_PX;
        // 偏差分析：PDF (tx[4],tx[5]) 转 viewport 得 (x0,y0)。vertical-rl 时首字在块右侧，
        // 若 left=x0 则整列在 x0 右侧约一字符宽 → 偏右；若 left=x0-width 则整列在 x0 左侧 → 偏左。
        // 故 PDF 参考点可能介于列左缘与右缘之间，用半宽偏移折中。
        const verticalLeft = x0 * scaleX - verticalBlockWidth * 0.5;
        textSpan.style.left = `${verticalLeft}px`;
        textSpan.style.top = `${y0 * scaleY}px`;
        // 不做 translateY 上移，避免整体和高亮偏高
        textSpan.style.transform = 'none';
        textSpan.style.transformOrigin = '0 100%';
        textSpan.style.writingMode = 'vertical-rl';
        textSpan.style.textOrientation = 'upright';
        textSpan.style.height = `${verticalBlockHeight}px`;
        textSpan.style.width = `${verticalBlockWidth}px`;
        // 供竖排高亮用：单字高度与 span 布局一致，mark 用 calc(N * var(--vertical-char-height)) 即可
        textSpan.style.setProperty('--vertical-char-height', `${canvasHeight}px`);
        // 调试：在 DOM 中可查看原始位置信息，便于确认偏差原因
        textSpan.setAttribute('data-pdf-x', String(tx[4]));
        textSpan.setAttribute('data-pdf-y', String(tx[5]));
        textSpan.setAttribute('data-viewport-x0', String(x0 * scaleX));
        textSpan.setAttribute('data-viewport-y0', String(y0 * scaleY));
        textSpan.setAttribute('data-vertical-width', String(verticalBlockWidth));
        textSpan.setAttribute('data-vertical', 'true');
    } else {
        // 根因修复：空白字符与过小 scaleX 的说明见上，不参与 scaleX 纠偏 / 竖排时已不走到此处
        if (wouldApplyScaleX) {
            const textScaleX = canvasWidth / naturalWidth;
            transforms.push(`scaleX(${textScaleX})`);
            textSpan.style.transform = transforms.join(' ');
            textSpan.style.transformOrigin = '0 100%';
        } else if (transforms.length > 0) {
            textSpan.style.transform = transforms.join(' ');
            textSpan.style.transformOrigin = '0 100%';
        }
    }

    textSpan.style.color = 'transparent';
    textSpan.style.opacity = '0';
    textSpan.style.mixBlendMode = 'multiply';

    return textSpan;
}

function setupTextLayerContainer(container: HTMLElement, viewport: any, offsetX: number = 0) {
    container.innerHTML = '';
    container.style.position = 'absolute';
    container.style.left = `${offsetX}px`; // 应用 canvas/svg 的偏移量，确保对齐
    container.style.top = '0';
    container.style.width = `${viewport.width}px`;
    container.style.height = `${viewport.height}px`;
    container.style.lineHeight = '1';
    container.style.userSelect = 'none';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'hidden';
}

export async function mountTextLayerToDom(
    container: HTMLElement,
    page: any,
    textContent: any,
    scale: number,
    renderPixelRadio?: number,
) {
    // biome-ignore lint/suspicious/noExplicitAny: 111
    const pdfjsLib = (window as any).pdfjsLib;
    if (!pdfjsLib || !textContent || !textContent.items) {
        return;
    }

    const parent = container.parentElement;
    let canvasOrSvg: HTMLElement | null = null;
    if (parent) {
        // 关键：SVG 渲染成功后 Canvas 仍在 DOM 中但可能被 display:none 隐藏。
        // 如果仍然优先选中隐藏的 Canvas，会导致 offsetLeft=0，从而让 TextLayer 与 SVG 居中位置错位。
        // 这里保持“Canvas 模式优先 Canvas”的同时，在 Canvas 不可见时回退到 svg 容器（不影响 Canvas 模式）。
        const canvas = parent.querySelector('canvas') as HTMLCanvasElement | null;
        const svgContainer = parent.querySelector('.pdf-svg-container') as HTMLElement | null;
        const svg = parent.querySelector('.pdf-svg-container svg') as SVGElement | null;

        const isElementVisible = (el: HTMLElement | null) => {
            if (!el) {
                return false;
            }
            // offsetParent 为 null 通常表示 display:none 或不在布局流中（例如 position:fixed 例外，但这里足够）
            if (el.offsetParent === null) {
                return false;
            }
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') {
                return false;
            }
            return true;
        };

        if (isElementVisible(canvas)) {
            canvasOrSvg = canvas;
        } else if (isElementVisible(svgContainer)) {
            canvasOrSvg = svgContainer;
        } else if (svg) {
            // 最后兜底：使用 svg 元素本身（某些布局下容器可能是 100% 宽导致 offsetLeft=0）
            canvasOrSvg = svg as unknown as HTMLElement;
        } else {
            canvasOrSvg = canvas || svgContainer;
        }
    }

    // 优先使用传入的 renderPixelRadio，确保与 Canvas 渲染使用相同的 pixelRatio
    // 如果没有传入，则从 Canvas 实际尺寸计算（作为降级方案）
    let pixelRatio = 1;
    if (renderPixelRadio) {
        pixelRatio = renderPixelRadio;
    } else if (canvasOrSvg && canvasOrSvg instanceof HTMLCanvasElement) {
        // 降级方案：从 Canvas 实际尺寸计算 pixelRatio
        const canvas = canvasOrSvg;
        const canvasWidth = canvas.width;
        const canvasClientWidth = canvas.clientWidth;
        if (canvasWidth > 0 && canvasClientWidth > 0 && canvasWidth !== canvasClientWidth) {
            pixelRatio = canvasWidth / canvasClientWidth;
        } else {
            // 如果无法从 Canvas 计算，使用默认的 getPixelRatio
            pixelRatio = getPixelRatio();
        }
    } else if (canvasOrSvg) {
        // SVG 渲染模式，pixelRatio 为 1
        pixelRatio = 1;
    }

    // 从 canvas/tile 容器的布局尺寸反推实际渲染 scale，确保文本层定位与画布一致。
    //
    // 背景：当 pdf-page-container 有 CSS transform: scale(cssTransformRatio) 时，
    // 容器内所有子元素都会被缩放。Canvas 以 effectiveRenderScale 渲染，CSS transform
    // 将其放大到视觉 scale。文本层作为同一容器的子元素也会被同样的 transform 缩放，
    // 因此文本层的定位也必须使用 effectiveRenderScale（而非视觉 scale），
    // 否则会产生 scale × cssTransformRatio = scale²/effectiveRenderScale 的"双重缩放"偏移。
    //
    // 非 Canvas 模式（SVG/Image）不使用 tier-based rendering，cssTransformRatio 恒为 1，
    // 此时 canvas.clientWidth 反推结果等于视觉 scale，行为与之前一致。
    let actualScale = scale;
    if (canvasOrSvg instanceof HTMLCanvasElement) {
        const pageWidthAtScale1 = page.getViewport({ scale: 1 }).width;
        if (pageWidthAtScale1 > 0) {
            let contentWidth: number;
            // 分块模式：canvas 是绝对定位的 tile，取 tile 容器宽度（= pageCssW）
            const isAbsoluteCanvas = window.getComputedStyle(canvasOrSvg).position === 'absolute';
            if (isAbsoluteCanvas && canvasOrSvg.parentElement) {
                contentWidth = canvasOrSvg.parentElement.clientWidth;
            } else {
                contentWidth = canvasOrSvg.clientWidth;
            }
            if (contentWidth > 0) {
                const derivedScale = contentWidth / pageWidthAtScale1;
                if (derivedScale > 0) {
                    actualScale = derivedScale;
                }
            }
        }
    } else if (canvasOrSvg) {
        // SVG 模式：优先使用 --scale-factor CSS 变量（如果与传入 scale 接近）
        const scaleFactorStr = window.getComputedStyle(canvasOrSvg).getPropertyValue('--scale-factor');
        if (scaleFactorStr) {
            const scaleFactor = Number(scaleFactorStr);
            if (scaleFactor > 0 && Math.abs(scaleFactor - scale) < 0.001) {
                actualScale = scaleFactor;
            }
        }
    }

    const renderViewport = page.getViewport({ scale: actualScale * pixelRatio });
    const displayViewport = page.getViewport({ scale: actualScale });
    const scaleX = displayViewport.width / renderViewport.width;
    const scaleY = displayViewport.height / renderViewport.height;
    const viewport = displayViewport;

    // pdf-page-container 保持 width:fit-content（= canvas/tile容器宽度），
    // canvas offsetLeft 始终为 0，文本层从左边界对齐即可，无需读取 offsetLeft。
    setupTextLayerContainer(container, viewport, 0);

    try {
        textContent.items.forEach((item: any) => {
            if (!('str' in item) || !item.str || !('transform' in item)) {
                return;
            }
            if (!('width' in item) || !('height' in item)) {
                return;
            }

            const textItem = item as TextItem;
            const textSpan = createTextSpan(textItem, renderViewport, scaleX, scaleY);

            if (container.parentNode) {
                container.appendChild(textSpan);
            }
        });
    } catch (error) {
        console.error('Manual text layer rendering failed:', error);
    }
}
